import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  ClipboardList, Plus, Target, Calendar, User, CheckCircle, Clock, AlertCircle
} from 'lucide-react';

export default function AssignmentsPage() {
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  // Form state
  const [form, setForm] = useState({ user_email: '', scenario_id: '', difficulty: 'intermediate', due_date: '' });

  const fetchData = useCallback(async () => {
    try {
      const api = axiosAuth();
      const [aRes, uRes, sRes] = await Promise.all([
        api.get('/admin/assignments'),
        api.get('/admin/users'),
        api.get('/scenarios'),
      ]);
      setAssignments(aRes.data.assignments || []);
      setUsers(uRes.data.users || []);
      setScenarios(sRes.data.scenarios || []);
    } catch (err) {
      if (err.response?.status === 403) { toast.error('Admin access required'); navigate('/'); }
      else toast.error('Failed to load data');
    } finally { setLoading(false); }
  }, [axiosAuth, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createAssignment = async () => {
    if (!form.user_email || !form.scenario_id) return toast.error('User and scenario required');
    try {
      await axiosAuth().post('/admin/assignments', form);
      toast.success('Assignment created');
      setForm({ user_email: '', scenario_id: '', difficulty: 'intermediate', due_date: '' });
      setShowCreate(false); fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'completed': return { bg: 'bg-success/10 text-success', icon: CheckCircle };
      case 'in_progress': return { bg: 'bg-info/10 text-info', icon: Clock };
      default: return { bg: 'bg-warning/10 text-warning', icon: AlertCircle };
    }
  };

  const getScenarioTitle = (id) => scenarios.find(s => s.id === id)?.title || id;
  const getUserName = (email) => users.find(u => u.email === email)?.name || email;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="heritage-card p-8 text-center"><div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="font-body text-sm">Loading...</p></div>
    </div>
  );

  const pendingCount = assignments.filter(a => a.status === 'pending').length;
  const completedCount = assignments.filter(a => a.status === 'completed').length;

  return (
    <div className="min-h-screen bg-background" data-testid="assignments-page">
      <Navbar showBack backTo="/admin" backLabel="Assignments" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Simulation Assignments</h1>
            <p className="font-body text-sm text-muted-foreground mt-1">Assign simulations to users and track completion</p>
          </div>
          <Button data-testid="create-assignment-btn" onClick={() => setShowCreate(true)}
            className="bg-brand text-[#333333] gap-1.5 h-9 px-4 text-sm font-medium rounded-lg hover:opacity-90">
            <Plus className="w-4 h-4" /> New Assignment
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="heritage-card p-4">
            <div className="flex items-center gap-2 mb-1"><ClipboardList className="w-4 h-4 text-brand" /><span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Total</span></div>
            <p className="font-heading text-xl font-bold">{assignments.length}</p>
          </div>
          <div className="heritage-card p-4">
            <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-warning" /><span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Pending</span></div>
            <p className="font-heading text-xl font-bold">{pendingCount}</p>
          </div>
          <div className="heritage-card p-4">
            <div className="flex items-center gap-2 mb-1"><CheckCircle className="w-4 h-4 text-success" /><span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Completed</span></div>
            <p className="font-heading text-xl font-bold">{completedCount}</p>
          </div>
        </div>

        {/* Assignment List */}
        {assignments.length === 0 ? (
          <div className="heritage-card p-8 text-center">
            <ClipboardList className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="font-heading text-sm font-bold mb-1">No Assignments Yet</p>
            <p className="font-body text-xs text-muted-foreground mb-4">Create your first simulation assignment</p>
            <Button onClick={() => setShowCreate(true)} className="bg-brand text-[#333333] gap-1.5 h-8 px-4 text-xs font-medium rounded-lg hover:opacity-90">
              <Plus className="w-3.5 h-3.5" /> Create Assignment
            </Button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="assignment-list">
            {assignments.map((a, i) => {
              const st = getStatusStyle(a.status);
              const StatusIcon = st.icon;
              return (
                <div key={a.id} className="heritage-card p-3 sm:p-4 flex items-center gap-3" data-testid={`assignment-${i}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${st.bg}`}>
                    <StatusIcon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-xs font-bold truncate">{getScenarioTitle(a.scenario_id)}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="font-body text-[10px] text-muted-foreground flex items-center gap-1"><User className="w-2.5 h-2.5" />{getUserName(a.user_email)}</span>
                      <Badge className="bg-muted text-muted-foreground border-none text-[9px] px-1 py-0">{a.difficulty}</Badge>
                      {a.due_date && <span className="font-body text-[10px] text-muted-foreground flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />Due: {a.due_date}</span>}
                    </div>
                  </div>
                  <Badge className={`${st.bg} border-none text-[10px] px-2 py-0.5 capitalize`}>{a.status}</Badge>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Create Assignment Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-md" data-testid="create-assignment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Create Assignment</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Assign a simulation scenario to a user</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="font-body text-xs text-muted-foreground mb-1 block">User</label>
              <Select value={form.user_email} onValueChange={val => setForm(p => ({ ...p, user_email: val }))}>
                <SelectTrigger data-testid="assignment-user-select" className="w-full border border-border h-9 font-body text-sm bg-surface">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent className="border border-border shadow-modal bg-surface max-h-60">
                  {users.map(u => <SelectItem key={u.id} value={u.email} className="font-body text-sm">{u.name} ({u.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-body text-xs text-muted-foreground mb-1 block">Scenario</label>
              <Select value={form.scenario_id} onValueChange={val => setForm(p => ({ ...p, scenario_id: val }))}>
                <SelectTrigger data-testid="assignment-scenario-select" className="w-full border border-border h-9 font-body text-sm bg-surface">
                  <SelectValue placeholder="Select scenario" />
                </SelectTrigger>
                <SelectContent className="border border-border shadow-modal bg-surface max-h-60">
                  {scenarios.map(s => <SelectItem key={s.id} value={s.id} className="font-body text-sm">{s.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-body text-xs text-muted-foreground mb-1 block">Difficulty</label>
                <Select value={form.difficulty} onValueChange={val => setForm(p => ({ ...p, difficulty: val }))}>
                  <SelectTrigger className="w-full border border-border h-9 font-body text-sm bg-surface"><SelectValue /></SelectTrigger>
                  <SelectContent className="border border-border shadow-modal bg-surface">
                    <SelectItem value="beginner" className="font-body text-sm">Beginner</SelectItem>
                    <SelectItem value="intermediate" className="font-body text-sm">Intermediate</SelectItem>
                    <SelectItem value="advanced" className="font-body text-sm">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-body text-xs text-muted-foreground mb-1 block">Due Date (optional)</label>
                <input data-testid="assignment-due-date" type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
                  className="heritage-input w-full text-sm h-9" />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={() => setShowCreate(false)} className="flex-1 h-9 bg-transparent border border-border text-foreground hover:bg-muted text-sm rounded-lg shadow-none">Cancel</Button>
              <Button data-testid="assignment-save-btn" onClick={createAssignment} className="flex-1 h-9 bg-brand text-[#333333] text-sm rounded-lg hover:opacity-90">Assign</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
